import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Hook to track time spent on different areas/sections of a candidate profile
 * @param isVisible - Whether the component is currently visible (not a different tab)
 * @param activeArea - The currently active area (e.g., 'bio-personal', 'bio-professional', 'issue-healthcare')
 * @returns Object with tracked times by area and a reset function
 */
export function useAreaTimeTracker(
  isVisible: boolean,
  activeArea: 'bios' | 'issues' | 'links' | null
) {
  const [trackedTimes, setTrackedTimes] = useState<Record<string, number>>({});
  const lastTickRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Start/stop tracking based on visibility and active area
  useEffect(() => {
    if (isVisible && activeArea) {
      // Start tracking
      lastTickRef.current = Date.now();
      
      // Set up interval to count every 3 seconds
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = lastTickRef.current ? (now - lastTickRef.current) / 1000 : 0;
        lastTickRef.current = now;

        if (elapsed > 0 && elapsed < 10) { // Only count if less than 10 seconds (avoid counting idle time)
          setTrackedTimes((prev) => ({
            ...prev,
            [activeArea]: (prev[activeArea] || 0) + elapsed,
          }));
        }
      }, 3000); // Tick every 3 seconds

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        lastTickRef.current = null;
      };
    } else {
      // Stop tracking
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      lastTickRef.current = null;
    }
  }, [isVisible, activeArea]);

  const resetTrackedTimes = useCallback(() => {
    setTrackedTimes({});
  }, []);

  const getAndResetTrackedTimes = useCallback(() => {
    const times = { ...trackedTimes };
    setTrackedTimes({});
    return times;
  }, [trackedTimes]);

  return {
    trackedTimes,
    resetTrackedTimes,
    getAndResetTrackedTimes,
  };
}
